
//#region import libraries
import React, { useEffect, useState, useMemo } from "react";

// custom components
import { CustomSpinner, NewScreen, Title } from "_atoms";
import { TableHeader, TableValue, CustomModal } from "_molecules";
import { AdminTable } from "_organisms";

// bootstrap
import { Container, Row, Button, Col, Form } from "react-bootstrap";
// redux
import { useSelector, useDispatch } from "react-redux";
import { selectUserDb, getDbInfo, selectFirebaseUser } from "_redux/slices/main-screen/user";

import {

  selectUnverifiedAccounts,
  reset,
  blockAccount,
  selectBlockStatus,
  selectTotalApprovedAccounts,
  selectTotalUnverifiedAccounts,
  getApprovedAccounts,
  selectApprovedAccounts,
  getUnverifiedAccounts,
  resetPassword,
  resetActionsFlags,
  selectResetPasswordStatus,
  selectGettingAccountsStatus,
  selectCurrentPage,
  selectRowsPerPage,
  setCurrentPage,
  setRowsPerPage
} from "_redux/slices/profile/admin-table";
import { verifyUser, selectVerifyStatus, reset as resetVerify } from "_redux/slices/analytics/new-users";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { Flex, Tab, TabList } from "@tremor/react";
import { selectAirportLogo, selectUploadAirportLogoStatus, uploadAirportLogo, getAirportLogo, resetAirportLogoFlags, selectAirportLogoStatus } from "_redux/slices/analytics/reviews";
import Pagination from "_scenes/portal/analytics/survey-answers/Pagination"; //in case
import ManualPagination from "_components/organisms/utils/manual-pagination";
import { generatePaginationRang } from "_utils/functions";
import CreateAdminForm from "_components/molecules/admin/create-admin-form";
//#endregion

const AdminPanel = () => {
  //#region redux
  const dispatch = useDispatch();

  const dbUser = useSelector(selectUserDb);
  const approvedAccounts = useSelector(selectApprovedAccounts);                 //data of approved accounts
  const unverifiedTable = useSelector(selectUnverifiedAccounts);                //data of unverified accounts
  const status = useSelector(selectGettingAccountsStatus);                      //status of fetching accounts (approved/unverified)
  const verifyStatus = useSelector(selectVerifyStatus);                         //status of verify user
  const blockStatus = useSelector(selectBlockStatus);                           //status  of block a user
  const resetPwdStatus = useSelector(selectResetPasswordStatus)                 //status of reset password
  const firebaseUser = useSelector(selectFirebaseUser);
  const airportLogo = useSelector(selectAirportLogo);                           //current logo of airport
  const uploadLogoStatus = useSelector(selectUploadAirportLogoStatus);          //status of posting logo
  const airportLogoStatus = useSelector(selectAirportLogoStatus);               //status of fetching logo
  //pagination
  const totalApprovedRows = useSelector(selectTotalApprovedAccounts);
  const totalUnverifiedRows = useSelector(selectTotalUnverifiedAccounts)
  const rowsPerPage = useSelector(selectRowsPerPage);
  const currentPage = useSelector(selectCurrentPage);
  //#endregion 

  //#region local state
  // modals
  const [successModal, setSuccessModal] = useState(false);
  const [failureModal, setFailureModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalBody, setModalBody] = useState("");
  const [buttonText, setButtonText] = useState("");
  //images/logo
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBorder, setImageBorder] = useState('grey');
  const [imageLoading, setImageLoading] = useState(false);
  const [changeLogo, setChangeLogo] = useState(false)
  //tabs
  const [selectedTab, setSelectedTab] = useState("1");
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);
  const [globalStatus, setGlobalStatus] = useState("IDLE")
  const [currentRowsRange, setCurrentRowsRange] = useState("");
  //#endregion

  //#region useEffect
  useEffect(() => {
    dispatch(reset());
    dispatch(resetVerify());
    dispatch(resetActionsFlags())
    if (!dbUser) {

      dispatch(getDbInfo(firebaseUser.uid));
    }
  }, []);

  useEffect(() => {
    if (selectedTab === "3") {
      dispatch(getAirportLogo(dbUser.details.authority));
    } else
      handleGetAccounts()
  }, [selectedTab, currentPage, rowsPerPage, dbUser])

  useEffect(() => {
    // if db user exsists get reviews info
    if (uploadLogoStatus === "succeeded") {
      dispatch(resetAirportLogoFlags());
      setChangeLogo(false)
      setSelectedImage(null)
    }
  }, [uploadLogoStatus]);


  useEffect(() => {

    const statuses = [blockStatus, verifyStatus, resetPwdStatus];

    if (statuses.includes("loading")) {
      setGlobalStatus("LOADING");
    } else if (statuses.some(status => ["succeeded", "failed"].includes(status))) {
      setGlobalStatus("IDLE");
    }


    const modalConfigs = [
      {
        status: blockStatus,
        success: {
          title: "Successfully Blocked Account",
          body: "The account has been successfully deleted and their access to the portal has been revoked.",
        },
        failure: {
          title: "Failed to Block Account",
          body: "The account has not been blocked, please try again later.",
        },
      },
      {
        status: resetPwdStatus,
        success: {
          title: "Successfully Sent Reset Password",
          body: "The email to reset the password has been sent. Please notify the user to check their inbox.",
        },
        failure: {
          title: "Failed to Send Reset Email",
          body: "The email has not been sent. Please try again or contact the PRM Assist development team.",
        },
      },
      {
        status: verifyStatus,
        success: {
          title: "Successfully Verified Account",
          body: "The account has been verified and can now log in to the system.",
        },
      },
    ];

    modalConfigs.forEach(({ status, success, failure }) => {
      if (status === "succeeded") {
        setModalTitle(success.title);
        setModalBody(success.body);
        setButtonText("Done");
        setSuccessModal(true);
      } else if (status === "failed" && failure) {
        setModalTitle(failure.title);
        setModalBody(failure.body);
        setButtonText("Go back");
        setFailureModal(true);
      }
    });

    if (statuses.includes("succeeded")) {
      dispatch(resetActionsFlags());
      dispatch(resetVerify());
      handleGetAccounts();

    }
  }, [blockStatus, resetPwdStatus, verifyStatus]);

  useEffect(() => {
    if (status === "succeeded") {
      const totalRows = selectedTab === "1" ? totalApprovedRows : totalUnverifiedRows;
      let paginationRowInfo = generatePaginationRang(
        totalRows,
        rowsPerPage,
        currentPage
      );
      setCurrentRowsRange(paginationRowInfo.currentRange);
    }
  }, [status])
  //#endregion

  //#region getting accounts
  const handleGetAccounts = () => {
    if (selectedTab === "3" || !dbUser) return;
    const params = {
      iata: dbUser.details.authority,
      skip: (currentPage - 1) * rowsPerPage,
      limit: rowsPerPage
    }
    if (selectedTab === "1") {
      dispatch(getApprovedAccounts({ ...params, approved: true }))
    }
    if (selectedTab === "2") {
      dispatch(getUnverifiedAccounts({ ...params, approved: false }))
    }
  }
  //#endregion

  //#region action functions
  const blockHandler = (user) => {

    dispatch(blockAccount(user.firebaseId));
  };

  const approveHandler = (user) => {
    const payload = { ...user, verified: true }

    dispatch(verifyUser(payload));
  };

  const resetPwd = (email) => {

    dispatch(resetPassword(email));
  };
  //#endregion

  //#region images/logo functions
  const handleImageSelect = (image) => {
    setSelectedImage(image);
    setImagePreview(URL.createObjectURL(image)); // Generate a preview    

  };

  const handleSaveLogo = () => {
    const data = { image: selectedImage, iata: dbUser.details.authority }
    dispatch(uploadAirportLogo(data))
  }
  const handleCancel = () => {
    setChangeLogo(false)
    setSelectedImage(null)
  }
  //#endregion

  //#region pagination
  //skip next rows (limit 10,25,50...)
  const showMore = () => {
    setSkip(skip + limit)
  }
  //skip to previous rows (limit 10,25,50...)
  const showLess = () => {
    setSkip(skip - limit)
  }
  //chnage number of rows per page
  const handleLimitChange = (event) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
  }

  const handleRowsPerPageChange = (newRowsPerPage) => {
    dispatch(setRowsPerPage(newRowsPerPage));
    const totalRows = selectedTab === "1" ? totalApprovedRows : totalUnverifiedRows;
    const newPageCount = Math.ceil(totalRows / newRowsPerPage);
    if (currentPage > newPageCount) {
      dispatch(setCurrentPage(newPageCount));
    }
  };
  //#endregion

  //#region buttons
  const ActionButton = ({ variant, onClick, loading, disabled, children, style = {} }) => (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={disabled || loading}
      style={{ width: 100, marginRight: 10, ...style }}
    >
      {children}
    </Button>
  );

  const UnverifiedBtns = ({ user, emailVerified, onBlock, onApprove }) => (
    <div style={{ textAlign: "center" }}>
      <ActionButton
        variant="outline-danger"
        onClick={() => onBlock(user)}
      >
        Block
      </ActionButton>

      <ActionButton
        variant="outline-primary"
        onClick={() => onApprove(user)}
        disabled={!emailVerified}
      >
        Approve
      </ActionButton>
    </div>
  );

  const ApprovedBtns = ({ user, email, onBlock, onResetPassword }) => (

    <div style={{ textAlign: "center" }}>
      <ActionButton
        variant="outline-primary"
        onClick={() => onResetPassword(email)}
      >
        Reset
      </ActionButton>

      <ActionButton
        variant="outline-danger"
        onClick={() => onBlock(user)}
      >
        Block
      </ActionButton>
    </div>
  );
  //#endregion

  //#region columns
  const columns = useMemo(
    () => [
      {
        Header: <TableHeader title={"Email"} />,
        accessor: "email",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Name"} />,
        accessor: "name",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Position"} />,
        accessor: "position",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Action"} />,
        accessor: "user",
        Cell: ({ row: { original } }) => (
          <UnverifiedBtns
            user={original.user}
            emailVerified={original.emailVerified}
            onBlock={blockHandler}
            onApprove={approveHandler}
          />
        ),
      },
    ],
    []
  );

  const approvedColumns = useMemo(
    () => [
      {
        Header: <TableHeader title={"Email"} />,
        accessor: "email",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Name"} />,
        accessor: "name",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Position"} />,
        accessor: "position",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Action"} />,
        accessor: "user",
        Cell: ({ row: { original } }) => (
          <ApprovedBtns
            user={original.user}         // pass the nested user
            email={original.email}       // pass the top-level email directly
            onBlock={blockHandler}
            onResetPassword={resetPwd}
          />
        ),
      },
    ],
    []
  );
  //#endregion


  return (
    <NewScreen>
      {/* Title & Subtitle */}
      <Row>
        <Col>
          <Title
            subtitle={"Admin Panel"}
            description={"View employee accounts and take actions on"}
          />
        </Col>
      </Row>

      <TabList
        defaultValue="1"
        onValueChange={(value) => setSelectedTab(value)}
        className="mt-6"
      >
        <Tab value="1" text="Portal Accounts" />
        <Tab value="2" text="Verify Accounts" />
        <Tab value="3" text="Logo" />
        {dbUser?.role === "GARETH" && <Tab value="4" text="Create Organization Admin" />}
      </TabList>


      {/* List of approved Users */}
      {
        selectedTab === "1" && (
          <>
            <Row className="mt-5">
              <p className="" style={{ fontSize: 26, fontWeight: "bold" }}>
                Portal Accounts
              </p>

              <div
                className="mt-3"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <p style={{ fontSize: 18, fontWeight: "600", opacity: 0.6 }}>
                  View accounts with access to your airport portal
                </p>
              </div>
            </Row>

            {status === "loading" ? (
              <Row className="justify-content-md-center">
                <CustomSpinner />
              </Row>
            ) : (
              <Container
                style={{
                  boxShadow: "0px 4px 8px 2px #9E9E9E",
                  borderRadius: "8px",
                  padding: "24px",
                  width: "100%",
                  marginBottom: "40px",
                  marginTop: "20px",
                  opacity: globalStatus === "LOADING" ? 0.6 : 1,
                }}
              >
                <AdminTable data={approvedAccounts ?? []} columns={approvedColumns} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "20px",
                  }}
                >

                  <ManualPagination
                    currentPage={currentPage}
                    setCurrentPage={(page) => dispatch(setCurrentPage(page))}
                    pageCount={Math.ceil(totalApprovedRows / rowsPerPage)}
                    currentRange={currentRowsRange}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={handleRowsPerPageChange}
                    totalCount={totalApprovedRows}
                    transaction={true}
                  />
                </div>
              </Container>
            )}

          </>
        )
      }

      {/* List of accounts need an action like approve or delete*/}
      {selectedTab === "2" &&
        <>
          <Row>

            <p className="mt-5" style={{ fontSize: 26, fontWeight: "bold" }}>
              Verify Accounts
            </p>

            <div
              className="mt-3"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <p style={{ fontSize: 18, fontWeight: "600", opacity: 0.6 }}>
                Verify your employee accounts to grant them access to PRM Portal{" "}
              </p>
            </div>
          </Row>

          {/* List of Users */}
          {status === "loading" ? (
            <Row className="justify-content-md-center">
              <CustomSpinner />
            </Row>
          ) : (
            <Container
              style={{
                boxShadow: "0px 4px 8px 2px #9E9E9E",
                opacity: globalStatus === "LOADING" ? 0.6 : 1,
                borderRadius: "8px",
                padding: "24px",
                width: "100%",
                marginBottom: "40px",
                marginTop: "20px",
                maxHeight: "600px",
                overflow: "hidden",        // Hide overflow except scroll container
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  overflowY: "auto",
                  maxHeight: "500px",
                  paddingRight: "10px", // prevent scrollbar overlap
                }}
              >
                <AdminTable data={unverifiedTable ?? []} columns={columns} />
              </div>


              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                }}
              >

                <ManualPagination
                  currentPage={currentPage}
                  setCurrentPage={(page) => dispatch(setCurrentPage(page))}
                  pageCount={Math.ceil(totalUnverifiedRows / rowsPerPage)}
                  currentRange={currentRowsRange}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={handleRowsPerPageChange}
                  totalCount={totalUnverifiedRows}
                  transaction={true}
                />
              </div>
            </Container>
          )}

        </>

      }

      {selectedTab === "3" &&
        <>
          <Row className="mt-5">
            <p className="" style={{ fontSize: 26, fontWeight: "bold" }}>
              {dbUser?.details.type === "AIRLINE" ? "Airline Logo" : " Airport Logo"}
            </p>

            <Col xs={12} md={4}>
              {changeLogo ?
                <>
                  {/* File Input */}
                  <Form.Label style={{ color: imageBorder }}>Image Input</Form.Label>
                  <Form.Control type="file" onChange={(e) => { handleImageSelect(e.target.files[0]) }} accept="image/*" style={{ borderColor: imageBorder }} />
                  <Button variant="outline-danger" onClick={handleCancel} className="mt-3">Cancel</Button>
                  {selectedImage && <Button variant="outline-primary" onClick={handleSaveLogo} className="mt-3 ms-2">Save Logo</Button>}
                </>
                :
                <>
                  <Button variant="primary" onClick={() => setChangeLogo(true)} className="mt-3">Change Logo</Button>
                </>

              }

            </Col>
            <Col Col xs={12} md={8}>
              <Flex>
                <Form.Label style={{ color: imageBorder }}>{dbUser?.details.type === "AIRLINE" ? "Airline Logo" : " Airport Logo"}</Form.Label>
                {
                  imageLoading == true || uploadLogoStatus === "loading" || airportLogoStatus === "loading" ? <div style={{}}><CustomSpinner size={true} rowCenter={true} /></div>
                    : uploadLogoStatus === "succeeded" ? <div style={{ display: 'flex' }}><p style={{ color: 'green', fontWeight: '300' }}>Uploaded Successfully</p><AiOutlineCloudUpload size={32} color={'green'} style={{ paddingBottom: 12 }} /></div> : ""
                }
              </Flex>
              <div style={{ height: 350 }} className="border">
                {
                  selectedImage == undefined && !airportLogo ?
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ color: 'white' }}>s</p>
                      <p style={{ fontSize: 18, opacity: 0.4, fontWeight: '300', marginTop: 100 }}>Select the 'choose file' button below to add an image for your airport</p>
                    </div>
                    : changeLogo && selectedImage ?
                      <img src={imagePreview} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 15 }} onLoad={() => { setImageLoading(false) }} />
                      : <img src={airportLogo || imagePreview} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 15 }} onLoad={() => { setImageLoading(false) }} />

                }
              </div>
            </Col>
          </Row>


        </>
      }

      {/* Create Organization Admin (super admin only) */}
      {selectedTab === "4" && dbUser?.role === "GARETH" &&
        <CreateAdminForm />
      }

      {/* Success Modal */}
      <CustomModal
        hide={() => { setSuccessModal(false) }}
        show={successModal}
        title={modalTitle}
        body={modalBody}
        button={buttonText}
        openButton={() => { setSuccessModal(false) }}
      />
      {/* Failure Modal */}
      <CustomModal
        hide={() => { setFailureModal(false) }}
        show={failureModal}
        title={modalTitle}
        body={modalBody}
        button={buttonText}
        openButton={() => { setFailureModal(false) }}
        failure={true}
      />
    </NewScreen>
  );
};

export default AdminPanel;
